import { CheckCircleOutlined, DownOutlined, PlusOutlined, SwapOutlined } from "@ant-design/icons";
import { Button, Checkbox, Popconfirm, Select, Typography } from "antd";
import { useCallback, useMemo, useState } from "react";

import { type ClientMealsPlanDetails, type MealsPlanDetails } from "@fitness-app/data-models";

import { getClientWebApiBaseUrl } from "~/modules/Sales/SalesAgent/constants";
import { useMealsPlanContextNullable } from "~/shared/providers/MealsPlanProvider";
import { supabase } from "~/store/initializeStore";
import { Logger } from "~/utils/Logger";

export type ProposedDishSummary = {
  id: string;
  name: string;
  portions: number | null;
  portionSize: number | null;
  calories: number | null;
  preparationSteps: string[];
  ingredients: Array<{
    id: string;
    name: string;
    portion: number;
    measure: string;
    multiplier?: number | null;
  }>;
  macros: {
    kcal: number;
    b: number;
    ww: number;
    t: number;
  };
};

const DAY_LABELS: Record<string, string> = {
  monday: "Poniedziałek",
  tuesday: "Wtorek",
  wednesday: "Środa",
  thursday: "Czwartek",
  friday: "Piątek",
  saturday: "Sobota",
  sunday: "Niedziela",
};

const MEAL_TYPE_LABELS: Record<string, string> = {
  BREAKFAST: "Śniadanie",
  SECOND_BREAKFAST: "Drugie śniadanie",
  LUNCH: "Lunch",
  DINNER: "Obiad",
  SNACK: "Przekąska",
  SUPPER: "Kolacja",
};

const FALLBACK_DAYS = Object.keys(DAY_LABELS);
const FALLBACK_MEAL_TYPES = Object.keys(MEAL_TYPE_LABELS);

const getPlanStructure = (planDetails: ClientMealsPlanDetails | MealsPlanDetails | undefined) => {
  const days = planDetails?.weeks?.[0]?.days;
  if (!days?.length) {
    return { dayNames: FALLBACK_DAYS, mealTypesByDay: null, dishCountByDayMeal: null };
  }

  return {
    dayNames: days.map((day) => day.name),
    mealTypesByDay: new Map(days.map((day) => [day.name, day.meals.map((meal) => meal.type as string)])),
    dishCountByDayMeal: new Map<string, number>(
      days.flatMap((day) =>
        day.meals.map((meal): [string, number] => [`${day.name}:${meal.type}`, meal.dishes.length]),
      ),
    ),
  };
};

const WEIGHT_MEASURE_NAMES = new Set(["g", "gram", "gramy", "ml", "mililitr"]);

const formatIngredientPortion = (ingredient: ProposedDishSummary["ingredients"][number]) => {
  const measure = ingredient.measure?.trim();
  const isUnitMeasure = !!measure && !WEIGHT_MEASURE_NAMES.has(measure.toLowerCase());
  if (ingredient.multiplier && isUnitMeasure) {
    return `${ingredient.multiplier} x ${measure} (${ingredient.portion} g)`;
  }
  return `${ingredient.portion} g`;
};

export const ProposedDishCard = ({
  dish,
  dishSpec,
  onAdded,
  planId,
  traineeId,
}: {
  dish: ProposedDishSummary;
  dishSpec: unknown;
  onAdded: () => void;
  planId: string;
  traineeId: string;
}) => {
  const context = useMealsPlanContextNullable();
  const { dayNames, mealTypesByDay, dishCountByDayMeal } = useMemo(
    () => getPlanStructure(context?.planDetails),
    [context?.planDetails],
  );

  const [dayName, setDayName] = useState<string | undefined>();
  const [mealType, setMealType] = useState<string | undefined>();
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [addedTo, setAddedTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mealTypeOptions = useMemo(() => {
    const types = (dayName && mealTypesByDay?.get(dayName)) || FALLBACK_MEAL_TYPES;
    return types.map((type) => ({ value: type, label: MEAL_TYPE_LABELS[type] ?? type }));
  }, [dayName, mealTypesByDay]);

  const handleAdd = useCallback(
    async (mode: "add" | "replace") => {
      if (!dayName || !mealType || isAdding) return;
      setIsAdding(true);
      setError(null);

      try {
        const token = (await supabase.auth.getSession()).data.session?.access_token;
        const response = await fetch(`${getClientWebApiBaseUrl()}/api/trainers/ai/nutrition-plan-add-dish`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            planId,
            traineeId,
            dayName,
            mealType,
            saveAsTemplate,
            mode,
            dish: dishSpec,
          }),
        });

        if (!response.ok) {
          const payload = (await response.json().catch(() => null)) as { error?: string } | null;
          throw new Error(payload?.error || `Nie udało się dodać potrawy (${response.status})`);
        }

        setAddedTo(
          `${mode === "replace" ? "Zastąpiono posiłek" : "Dodano do planu"}: ${DAY_LABELS[dayName] ?? dayName} – ${
            MEAL_TYPE_LABELS[mealType] ?? mealType
          }`,
        );
        onAdded();
      } catch (addError) {
        Logger.error(addError, `[nutrition-plan-chat] failed to add proposed dish to plan ${planId}`);
        setError(addError instanceof Error ? addError.message : "Nie udało się dodać potrawy.");
      } finally {
        setIsAdding(false);
      }
    },
    [dayName, dishSpec, isAdding, mealType, onAdded, planId, saveAsTemplate, traineeId],
  );

  const replaceTargetCount = dayName && mealType ? (dishCountByDayMeal?.get(`${dayName}:${mealType}`) ?? null) : null;

  return (
    <div className="overflow-hidden rounded-lg border border-emerald-200 bg-white text-xs shadow-sm">
      <div className="space-y-2 px-3 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <span className="block text-[10px] font-medium uppercase tracking-wide text-emerald-600">
              Propozycja potrawy
            </span>
            <span className="block truncate text-sm font-semibold text-gray-900">{dish.name}</span>
          </div>
          <span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">
            {dish.macros.kcal} kcal
          </span>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <span className="rounded-md bg-gray-100 px-2 py-0.5 text-gray-700">B {dish.macros.b} g</span>
          <span className="rounded-md bg-gray-100 px-2 py-0.5 text-gray-700">WW {dish.macros.ww} g</span>
          <span className="rounded-md bg-gray-100 px-2 py-0.5 text-gray-700">T {dish.macros.t} g</span>
          {dish.portions ? (
            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-gray-700">
              {dish.portions} {dish.portions === 1 ? "porcja" : "porcje"}
            </span>
          ) : null}
        </div>

        <ul className="m-0 list-none space-y-1 p-0">
          {dish.ingredients.map((ingredient) => (
            <li key={ingredient.id} className="flex items-baseline justify-between gap-2">
              <span className="min-w-0 truncate text-gray-700">{ingredient.name}</span>
              <span className="shrink-0 text-gray-500">{formatIngredientPortion(ingredient)}</span>
            </li>
          ))}
        </ul>

        {dish.preparationSteps.length ? (
          <div>
            <button
              type="button"
              className="flex items-center gap-1 text-[11px] font-medium text-gray-500 transition-colors hover:text-gray-800"
              onClick={() => setShowSteps((value) => !value)}
            >
              Przygotowanie
              <DownOutlined className={["text-[9px] transition-transform", showSteps ? "rotate-180" : ""].join(" ")} />
            </button>
            {showSteps ? (
              <ol className="mb-0 mt-1 space-y-1 pl-4 text-gray-600">
                {dish.preparationSteps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="space-y-2 border-t border-gray-100 bg-gray-50 px-3 py-3">
        {addedTo ? (
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircleOutlined />
            <span className="font-medium">{addedTo}</span>
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              <Select
                size="small"
                className="flex-1"
                placeholder="Dzień"
                value={dayName}
                onChange={(value) => setDayName(value)}
                options={dayNames.map((name) => ({ value: name, label: DAY_LABELS[name] ?? name }))}
              />
              <Select
                size="small"
                className="flex-1"
                placeholder="Posiłek"
                value={mealType}
                onChange={(value) => setMealType(value)}
                options={mealTypeOptions}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Checkbox
                checked={saveAsTemplate}
                onChange={(event) => setSaveAsTemplate(event.target.checked)}
                className="text-[11px] text-gray-600"
              >
                Zapisz też jako szablon
              </Checkbox>
              <div className="grid grid-cols-2 gap-1.5">
                <Popconfirm
                  title="Zastąpić cały posiłek?"
                  description={
                    replaceTargetCount
                      ? `Z posiłku zostanie usuniętych ${replaceTargetCount} ${replaceTargetCount === 1 ? "pozycja" : "pozycji"}, zostanie tylko nowa potrawa.`
                      : "Wszystkie pozycje tego posiłku zostaną usunięte, zostanie tylko nowa potrawa."
                  }
                  okText="Tak, zastąp"
                  cancelText="Anuluj"
                  okButtonProps={{ danger: true }}
                  disabled={!dayName || !mealType || isAdding}
                  onConfirm={() => void handleAdd("replace")}
                >
                  <Button
                    block
                    size="small"
                    icon={<SwapOutlined />}
                    loading={isAdding}
                    disabled={!dayName || !mealType}
                  >
                    Zastąp posiłek
                  </Button>
                </Popconfirm>
                <Button
                  block
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  loading={isAdding}
                  disabled={!dayName || !mealType}
                  onClick={() => void handleAdd("add")}
                >
                  Dodaj do planu
                </Button>
              </div>
            </div>
            {error ? <Typography.Text type="danger">{error}</Typography.Text> : null}
          </>
        )}
      </div>
    </div>
  );
};
